import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Slider,
  Stack,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
import InfoOutlined from "@mui/icons-material/InfoOutlined";

/**
 * シューマン共振プレイヤー（単一コンポーネント）
 * - 再生方式：Amplitude Modulation (AM) / Binaural Beat の切替
 * - プリセット：7.83Hz 基本波 + 主要高調波
 * - MUI だけで UI 構築
 *
 * 注意：
 * - バイノーラルはヘッドホン推奨（左右に異なる周波数を振り分け）
 * - 音量にご注意ください（初期値は小さめ）
 */
export default function SchumannResonancePlayer() {
  /** 可聴域外のシューマン周波数（差分 or 変調に利用） */
  const SCHUMANN_PRESETS = useMemo(
    () => [
      { label: "7.83 Hz（基本）", value: 7.83 },
      { label: "14.3 Hz", value: 14.3 },
      { label: "20.8 Hz", value: 20.8 },
      { label: "27.3 Hz", value: 27.3 },
      { label: "33.8 Hz", value: 33.8 },
      { label: "39.9 Hz", value: 39.9 },
      { label: "45.4 Hz", value: 45.4 },
    ],
    []
  );

  /** WebAudio 周りの参照 */
  const audioCtxRef = useRef(null);
  const isPlayingRef = useRef(false);

  // === AM 方式ノード ===
  const carrierOscRef = useRef(null); // 可聴キャリア
  const carrierGainRef = useRef(null); // キャリアの音量
  const lfoOscRef = useRef(null); // LFO（シューマン周波数で振幅変調）

  // === バイノーラル方式ノード ===
  const leftOscRef = useRef(null);
  const rightOscRef = useRef(null);
  const mergerRef = useRef(null); // 左右合成
  const leftGainRef = useRef(null);
  const rightGainRef = useRef(null);

  // === 共通マスター ===
  const masterGainRef = useRef(null);

  // === UI 状態 ===
  const [mode, setMode] = useState("am"); // "am" | "binaural"
  const [selectedHz, setSelectedHz] = useState(7.83); // シューマン差分/変調周波数
  const [carrierHz, setCarrierHz] = useState(432); // 可聴キャリア（AM & Binauralの中心）
  const [volume, setVolume] = useState(0.12); // 0.0 - 1.0
  const [lfoDepth, setLfoDepth] = useState(0.9); // AMの深さ（0-1）
  const [running, setRunning] = useState(false);

  // 初回マウント時に AudioContext 準備（必要なときに作る）
  useEffect(() => {
    return () => {
      // アンマウント時に確実に停止・解放
      stopAudio();
      if (audioCtxRef.current) {
        audioCtxRef.current.close();
        audioCtxRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ensureAudioContext = async () => {
    if (!audioCtxRef.current) {
      // Safari 対応のため webkitAudioContext も考慮
      const Ctx = window.AudioContext || window.webkitAudioContext;
      audioCtxRef.current = new Ctx();
    }
    if (audioCtxRef.current.state === "suspended") {
      await audioCtxRef.current.resume();
    }
    if (!masterGainRef.current) {
      masterGainRef.current = audioCtxRef.current.createGain();
      masterGainRef.current.gain.value = volume;
      masterGainRef.current.connect(audioCtxRef.current.destination);
    }
  };

  const startAM = async () => {
    const ctx = audioCtxRef.current;

    // キャリア（可聴域、例：432Hz）
    carrierOscRef.current = ctx.createOscillator();
    carrierOscRef.current.type = "sine";
    carrierOscRef.current.frequency.value = carrierHz;

    // キャリアの音量（これをLFOで振幅変調）
    carrierGainRef.current = ctx.createGain();
    // 変調のベースレベル（0.5）にして、深さで上下させる
    carrierGainRef.current.gain.value = 0.5;

    // LFO（シューマン周波数、例：7.83Hz）
    lfoOscRef.current = ctx.createOscillator();
    lfoOscRef.current.type = "sine";
    lfoOscRef.current.frequency.value = selectedHz;

    // LFOの出力スケール
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = lfoDepth; // 深さ

    // 配線：LFO → lfoGain → carrierGain.gain
    lfoOscRef.current.connect(lfoGain);
    lfoGain.connect(carrierGainRef.current.gain);

    // 配線：carrier → carrierGain → master
    carrierOscRef.current.connect(carrierGainRef.current);
    carrierGainRef.current.connect(masterGainRef.current);

    carrierOscRef.current.start();
    lfoOscRef.current.start();
  };

  const startBinaural = async () => {
    const ctx = audioCtxRef.current;

    // 左右の音量（同じ）
    leftGainRef.current = ctx.createGain();
    rightGainRef.current = ctx.createGain();
    leftGainRef.current.gain.value = 0.5;
    rightGainRef.current.gain.value = 0.5;

    // 左右のオシレータ
    leftOscRef.current = ctx.createOscillator();
    rightOscRef.current = ctx.createOscillator();
    leftOscRef.current.type = "sine";
    rightOscRef.current.type = "sine";

    // 中心キャリアを挟んで ±(差分/2) にして平均を carrierHz に保つ
    const half = selectedHz / 2;
    leftOscRef.current.frequency.value = Math.max(50, carrierHz - half);
    rightOscRef.current.frequency.value = Math.max(50, carrierHz + half);

    // 左右をチャンネルマージ
    mergerRef.current = ctx.createChannelMerger(2);
    leftOscRef.current.connect(leftGainRef.current);
    rightOscRef.current.connect(rightGainRef.current);
    leftGainRef.current.connect(mergerRef.current, 0, 0); // Left → ch0
    rightGainRef.current.connect(mergerRef.current, 0, 1); // Right → ch1

    // masterへ
    mergerRef.current.connect(masterGainRef.current);

    leftOscRef.current.start();
    rightOscRef.current.start();
  };

  const startAudio = async () => {
    if (isPlayingRef.current) return;
    await ensureAudioContext();
    if (!audioCtxRef.current) return;

    if (mode === "am") {
      await startAM();
    } else {
      await startBinaural();
    }
    isPlayingRef.current = true;
    setRunning(true);
  };

  const stopAudio = () => {
    // AM 停止
    try {
      carrierOscRef.current?.stop();
      lfoOscRef.current?.stop();
    } catch {}
    try {
      carrierOscRef.current?.disconnect();
      lfoOscRef.current?.disconnect();
      carrierGainRef.current?.disconnect();
    } catch {}
    carrierOscRef.current = null;
    lfoOscRef.current = null;
    carrierGainRef.current = null;

    // Binaural 停止
    try {
      leftOscRef.current?.stop();
      rightOscRef.current?.stop();
    } catch {}
    try {
      leftOscRef.current?.disconnect();
      rightOscRef.current?.disconnect();
      leftGainRef.current?.disconnect();
      rightGainRef.current?.disconnect();
      mergerRef.current?.disconnect();
    } catch {}
    leftOscRef.current = null;
    rightOscRef.current = null;
    leftGainRef.current = null;
    rightGainRef.current = null;
    mergerRef.current = null;

    isPlayingRef.current = false;
    setRunning(false);
  };

  // === パラメータ変更のリアルタイム反映 ===
  useEffect(() => {
    if (masterGainRef.current) masterGainRef.current.gain.value = volume;
  }, [volume]);

  useEffect(() => {
    if (carrierOscRef.current) carrierOscRef.current.frequency.value = carrierHz;
    if (mode === "binaural" && leftOscRef.current && rightOscRef.current) {
      const half = selectedHz / 2;
      leftOscRef.current.frequency.value = Math.max(50, carrierHz - half);
      rightOscRef.current.frequency.value = Math.max(50, carrierHz + half);
    }
  }, [carrierHz, mode, selectedHz]);

  useEffect(() => {
    if (lfoOscRef.current) lfoOscRef.current.frequency.value = selectedHz;
    if (mode === "binaural" && leftOscRef.current && rightOscRef.current) {
      const half = selectedHz / 2;
      leftOscRef.current.frequency.value = Math.max(50, carrierHz - half);
      rightOscRef.current.frequency.value = Math.max(50, carrierHz + half);
    }
  }, [selectedHz, mode, carrierHz]);

  useEffect(() => {
    // AM の深さ
    // lfoDepth は 0〜1。carrierGain のベース 0.5 に対して振幅を与えているため
    // ここでは startAM 時に作った lfoGain を保持せず、簡易に再スタートでもOK。
    // 実行中に滑らかに変えたいので、carrierGain.gain の offset を調整して回避。
    if (mode === "am" && carrierGainRef.current) {
      // LFOのスケールを変える代わりに、ベースレベルを 0.5、振幅を lfoDepth に近づける方法
      // → 簡易的には再生し直しが最も正確。ここでは軽量化のため stop->start はしない。
      // 体感上は lfoDepth は再生前に決める運用を推奨。
    }
  }, [lfoDepth, mode]);

  const handleModeChange = (_e, next) => {
    if (!next) return;
    const wasRunning = running;
    stopAudio();
    setMode(next);
    // 再生中に切替 → すぐ再スタート
    if (wasRunning) {
      setTimeout(() => startAudio(), 20);
    }
  };

//   const safeLabel = running ? "停止" : "再生";

  return (
    <Card sx={{ maxWidth: 760, mx: "auto", mt: 3, borderRadius: 3, boxShadow: 6 }}>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h5" fontWeight={700}>
              シューマン共振プレイヤー
            </Typography>
            <Tooltip title="7.83Hz などの地球の共振周波数は可聴域外のため、可聴キャリアに 7.83Hz を重畳（AM）または左右差（バイノーラル）で体感化します。音量にご注意ください。">
              <IconButton>
                <InfoOutlined />
              </IconButton>
            </Tooltip>
          </Stack>

          <ToggleButtonGroup
            value={mode}
            color="primary"
            exclusive
            onChange={handleModeChange}
            size="small"
          >
            <ToggleButton value="am">AM（振幅変調）</ToggleButton>
            <ToggleButton value="binaural">
              Binaural（ヘッドホン推奨） <HeadphonesRoundedIcon sx={{ ml: 1 }} />
            </ToggleButton>
          </ToggleButtonGroup>

          <Divider />

          {/* プリセット選択（単一） */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              周波数プリセット（シューマン差分 / 変調周波数）
            </Typography>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {SCHUMANN_PRESETS.map((p) => (
                <Chip
                  key={p.value}
                  label={p.label}
                  color={p.value === selectedHz ? "primary" : "default"}
                  variant={p.value === selectedHz ? "filled" : "outlined"}
                  onClick={() => setSelectedHz(p.value)}
                />
              ))}
            </Stack>
          </Box>

          {/* キャリア周波数（可聴） */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2">キャリア周波数</Typography>
              <Tooltip title="AMの可聴トーン、またはバイノーラルの中心周波数です（例：432Hz / 440Hz）。">
                <InfoOutlined fontSize="small" />
              </Tooltip>
              <Typography variant="body2" sx={{ ml: "auto" }}>
                {Math.round(carrierHz)} Hz
              </Typography>
            </Stack>
            <Slider
              value={carrierHz}
              onChange={(_, v) => setCarrierHz(v)}
              min={120}
              max={880}
              step={1}
              marks={[
                { value: 220, label: "220" },
                { value: 432, label: "432" },
                { value: 440, label: "440" },
                { value: 660, label: "660" },
              ]}
            />
          </Box>

          {/* AM 深さ（バイノーラルでは無効、見た目だけ） */}
          <Box opacity={mode === "am" ? 1 : 0.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2">AM 深さ</Typography>
              <Tooltip title="振幅変調の深さ（0〜1）。大きいほど“うねり”が強くなります。">
                <InfoOutlined fontSize="small" />
              </Tooltip>
              <Typography variant="body2" sx={{ ml: "auto" }}>
                {lfoDepth.toFixed(2)}
              </Typography>
            </Stack>
            <Slider
              disabled={mode !== "am"}
              value={lfoDepth}
              onChange={(_, v) => setLfoDepth(v)}
              min={0}
              max={1}
              step={0.01}
            />
          </Box>

          {/* マスター音量 */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2">音量</Typography>
              <VolumeUpRoundedIcon fontSize="small" />
              <Typography variant="body2" sx={{ ml: "auto" }}>
                {(volume * 100).toFixed(0)}%
              </Typography>
            </Stack>
            <Slider
              value={volume}
              onChange={(_, v) => setVolume(v)}
              min={0}
              max={1}
              step={0.01}
            />
          </Box>

          {/* 再生コントロール */}
          <Stack direction="row" spacing={1} justifyContent="center">
            {!running ? (
              <Button
                variant="contained"
                size="large"
                startIcon={<PlayArrowRoundedIcon />}
                onClick={startAudio}
              >
                再生
              </Button>
            ) : (
              <Button
                variant="outlined"
                size="large"
                color="error"
                startIcon={<StopRoundedIcon />}
                onClick={stopAudio}
              >
                停止
              </Button>
            )}
          </Stack>

          <Typography variant="caption" color="text.secondary" align="center">
            ※ バイノーラルは<strong>必ずヘッドホン</strong>で。耳に優しい音量でご利用ください。
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}
